<template>
  <div id="app">
    <Header />
    <Nav />
    <main>
      <transition name="fade">
        <router-view />
      </transition>
    </main>

    <app-no-connection :disabled="cacheLoaded"></app-no-connection>
    <app-cover :disabled="isCover"></app-cover>
  </div>
</template>

<script>
import Header from '@/layout/Header.vue'
import Nav from '@/layout/Nav.vue'
import NoConnection from '@/layout/NoConnection.vue'
import Cover from '@/layout/Cover.vue'

export default {
  name: 'App',
  computed: {
    cacheLoaded() {
      return this.$store.state.cacheLoaded
    },
    competitionName() {
      return this.$store.state.about.competition
    },
    isCover() {
      return !this.$store.state.cache.live.disabled
    },
  },
  watch: {
    competitionName(newName) {
      document.title = `${newName} - Wyniki zawodów łuczniczych | TuPostrzelasz.pl`
    },
  },
  components: {
    Header,
    Nav,
    appNoConnection: NoConnection,
    appCover: Cover,
  },
  sockets: {
    emitCache(data) {
      this.$store.commit('setCache', data)
    },
    emitImage(image) {
      this.$store.commit('setImage', image)
    },
  },
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css?family=Montserrat:200,300,400,600&subset=latin-ext');
@import '@/scss/_variables.scss';
@import '@/scss/_mixins.scss';

body {
  margin: 0;
}

#app {
  font-family: 'Montserrat', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--dark);
  background-color: #f5f6fa;
  min-height: 100vh;
  font-size: 16px;
  display: grid;
  grid-template-columns: 64px 1fr;
  overflow: hidden;

  @media #{$media-sm} {
    grid-template-columns: 1fr;
  }
}

:root {
  --green: #134532;
  --primary: #34baba;
  --semiPrimary: #9cd3d3;
  --orange: #e8630a;
  --dark: #2b4353;
  --bg: #f5f6fa;
  --font: 'Montserrat', sans-serif;
}

::selection {
  background-color: var(--primary);
  color: #fff;
}

::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
::-webkit-scrollbar-track {
  background: var(--bg);
}
::-webkit-scrollbar-thumb {
  background: var(--dark);
  &:horizontal {
    background: $orange;
  }
}

main {
  width: 100%;
  height: calc(100vh - 64px);
  box-sizing: border-box;
  margin: 0 auto;
  padding: 32px;
  overflow: auto;
  overflow-x: hidden;

  @media #{$media-xl} {
    padding: 16px;
  }

  @media #{$media-md} {
    padding: 8px;
  }

  @media #{$media-sm} {
    padding: 8px;
    margin-top: 55px;
    height: calc(100vh - 100px);
  }
}

h2 {
  margin-bottom: 10px;
}

h3.sub {
  margin: 0;
  margin-top: 5px;
  opacity: 0.6;
}

.wrapper {
  width: 1000px;
  margin: 0 auto;

  @media #{$media-xl} {
    width: 100%;
  }
}

section {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  border: solid 1px #edeff6;
}

.outer-table {
  width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
  margin: 16px auto;

  @media #{$media-xl} {
    width: calc(100vw - 96px);
  }

  @media #{$media-md} {
    width: calc(100vw - 80px);
  }

  @media #{$media-sm} {
    width: calc(100vw - 54px);
  }
}

table {
  min-width: 100%;
  table-layout: fixed;
  white-space: nowrap;
  border-collapse: collapse;
  box-shadow: 0px 0px 0px 2px #edeff6;
  box-sizing: border-box;
  background-color: #fff;
  border-radius: 4px;
  overflow: hidden;

  @media #{$media-sm} {
    font-size: 0.8rem;
  }

  td,
  th {
    padding: 8px 6px;
  }

  i {
    font-size: 0.8em;
    color: var(--orange);
  }

  .header th {
    color: var(--primary);
    background-color: #fff;
    font-weight: 300;
    font-style: italic;
    border-bottom: solid 1px var(--primary);
  }

  tr:nth-of-type(odd) {
    background-color: #f9f9fe;
  }
}

// Anim
.fade-enter-active {
  transition: opacity 1s ease;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.list-move {
  transition: transform 1s;
}

.buttons-set {
  display: inline-flex;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0px 0px 0px 2px #edeff6;

  &__button {
    display: block;
    padding: 6px 8px;
    color: $dark;
    border-right: solid 2px #edeff6;
    text-decoration: none;
    background-color: #fff;
    transition: 0.3s;
    font-size: 0.9rem;

    &--active {
      background-color: $orange;
      color: #fff;
    }
  }
}

.select {
  display: inline-block;
  border-radius: 16px;
  box-shadow: 0px 0px 0px 2px #edeff6;
  outline: none;
  border: 0;
  background: #fff;
  position: relative;
  font-size: 0.9em;

  select {
    all: unset;
    display: inline-block;
    padding: 6px 12px;
    padding-right: 32px;
  }

  option {
    padding: 4px;
  }

  &::after {
    @extend %pseudo-fontawesome;
    content: '\f0dd';
    position: absolute;
    top: 5px;
    right: 10px;
    pointer-events: none;
  }
}

.btn {
  all: unset;
  background-color: var(--primary);
  padding: 8px;
  color: #fff;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  font-size: 0.8em;
  transition: 0.3s;
  font-family: var(--font);

  &:hover {
    opacity: 0.8;
  }
}

.wysiwyg-content {
  text-align: left;

  img {
    box-sizing: border-box;
    max-width: calc(100% - 20px);
    margin: 10px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    text-align: center;
  }

  a {
    color: var(--primary);
    font-weight: 600;
  }

  // Harmonogram
  ol {
    list-style: none;
    text-align: left;
    padding-left: 6px;

    @media #{$media-sm} {
      font-size: 0.9em;
    }

    > li {
      margin-top: 12px;

      > b,
      > strong {
        display: block;
        color: var(--orange);
        margin-bottom: 4px;

        @media #{$media-md} {
          text-align: center;
        }
      }
    }

    ul {
      list-style-type: none;
      padding-left: 0;

      li {
        display: flex;

        @media #{$media-sm} {
          display: block;
          margin-bottom: 5px;
        }
      }

      b,
      strong {
        flex: 0 0 95px;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        text-align: right;
        padding: 2px;
        padding-right: 8px;
        margin-right: 8px;
        font-size: 0.8em;
        box-sizing: border-box;
        border-right: 1px dashed #888;

        @media #{$media-sm} {
          display: inline;
          margin-right: 4px;
          flex: none;
          border: none;
          font-size: inherit;
          padding: 0;

          &::after {
            content: ':';
          }
        }
      }
    }
  }
}
</style>
